import React, { useState, useEffect } from "react";
import {
  Filter,
  Plus,
  Search,
  Loader2,
  AlertCircle,
} from "lucide-react";
import TextInputComp from "../../../components/customer/TextInputComp";
import Dropdown, { Option } from "../../../components/customer/Dropdown";
import { petfilterOptions } from "../../../utils/Customer/CustomerData";
import PetCardComponent from "../../../components/customer/PetSectionComponents.tsx/PetCardComponent";
import { useNavigate } from "react-router-dom";
import CommonButton from "../../../components/CommonButton";

import {
  IPetFilterRequest,
  PetSellType,
} from "../../../types/Request/AdminRequest";
import {
  useLazyGetAllPetsQuery,
  useUpdatePetStatusMutation,
} from "../../../redux/actions/Admin/PetsAdmin";

export default function AdminPetScreen() {
  const navigate = useNavigate();

  // --- Core API Hooks ---
  // Fix 1: Removed `= []` default in destructuring to prevent reference changes on every render
  const [
    fetchPetsTrigger,
    { data: serverPetsList, isLoading: petsLoading, isError: petsError },
  ] = useLazyGetAllPetsQuery();
  
  const [updatePetStatus] = useUpdatePetStatusMutation();
  const [petsData, setPetsData] = useState<any[]>([]);

  // --- Component Local States ---
  const [selectedFilter, setSelectedFilter] = useState<Option | null>(null);
  const [searchText, setSearchText] = useState("");

  const executeFilteredQuery = (currentFilter: Option | null) => {
    const apiPayload: IPetFilterRequest = {};

    if (currentFilter) {
      const val = currentFilter.value.toLowerCase();
      if (val === "dogs") apiPayload.category = "Dog";
      if (val === "cats") apiPayload.category = "Cat";
      if (val === "adopt") apiPayload.petType = PetSellType.Adopt;
      if (val === "buy") apiPayload.petType = PetSellType.Sell;
    }

    fetchPetsTrigger(apiPayload);
  };

  // Fetch data on initial mount and whenever category dropdown changes
  useEffect(() => {
    executeFilteredQuery(selectedFilter);
  }, [selectedFilter]);

  // Fix 2: Only update local state when serverPetsList actually arrives
  useEffect(() => {
    if (serverPetsList && Array.isArray(serverPetsList)) {
      setPetsData(serverPetsList);
    }
  }, [serverPetsList]);

  const handleFilterChange = (option: Option) => {
    setSelectedFilter(option);
  };

  const handleSearchTextChange = (value: string) => {
    setSearchText(value);
  };

  const handleEditPetClick = (pet: any) => {
    navigate("editScreen", { state: { petData: pet } });
  };

  const handleClickAdd = () => {
    navigate("addScreen");
  };

  // --- Local Search Filtering ---
  const filteredPets = petsData.filter((pet: any) => {
    if (!searchText.trim()) return true;

    const search = searchText.toLowerCase().trim();

    return (
      pet.petName?.toLowerCase().includes(search) ||
      pet.breed?.toLowerCase().includes(search) ||
      pet.microchipId?.toLowerCase().includes(search)
    );
  });

  return (
    <div className="p-4 md:p-6 font-nunito antialiased max-w-[1400px] mx-auto space-y-6">
      {/* Top Header Row Container */}
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center border-b border-gray-100 pb-5 gap-4">
        <div>
          <h1 className="text-2xl font-black text-logoHeading tracking-tight">
            Pet Management
          </h1>
          <p className="text-xs font-medium text-gray-400 mt-1">
            Manage your pets, update their status, and edit their details.
          </p>
        </div>

        <div className="w-full lg:w-auto shrink-0 flex justify-end">
          <CommonButton
            text="Add New Pet"
            icon={<Plus size={15} className="stroke-[3]" />}
            iconPosition="left"
            variant="filled"
            rounded="md"
            className="!w-full sm:!w-auto font-bold uppercase tracking-wider text-xs shadow-2xs"
            iconTextClassName="px-5 py-2.5"
            onClick={handleClickAdd}
          />
        </div>
      </div>

      {/* FILTER CONTROLS BAR CONTAINER */}
      <div className="bg-white rounded-2xl p-4 shadow-xs border border-mediumgray flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div className="relative w-full md:max-w-md flex-1">
          <Search
            size={16}
            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 z-10"
          />
          <TextInputComp
            placeholder="Search by pet name, breed, or microchip ID..."
            value={searchText}
            onChangeText={handleSearchTextChange}
            className="pl-10 w-full text-xs font-semibold focus:border-mainBrandColor"
          />
        </div>

        <div className="w-full md:w-[180px] shrink-0">
          <Dropdown
            options={petfilterOptions}
            value={selectedFilter}
            onChange={handleFilterChange}
            dropdownIcon={<Filter size={16} className="text-logoHeading" />}
          />
        </div>
      </div>

      {/* WORKSPACE DATA MATRIX STAGE */}
      {petsLoading ? (
        <div className="flex flex-col items-center justify-center py-28 bg-white rounded-2xl border border-mediumgray shadow-2xs">
          <Loader2 className="w-8 h-8 text-mainBrandColor animate-spin" />
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mt-3">
            Loading pets...
          </p>
        </div>
      ) : petsError ? (
        <div className="bg-white rounded-2xl p-8 text-center border border-red-100 max-w-md mx-auto shadow-2xs">
          <AlertCircle className="w-10 h-10 text-red-500 mx-auto" />
          <p className="text-sm font-bold text-gray-800 mt-2">
            Something went wrong while loading pets. Please try again.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          <PetCardComponent
            handleBooknow={handleEditPetClick}
            data={filteredPets}
            showButton={false}
          />
        </div>
      )}
    </div>
  );
}